import React, { useState, useRef, useEffect, useContext } from 'react';
import XProvider from '@ant-design/x/es/x-provider';
import Bubble from '@ant-design/x/es/bubble';
import Sender from '@ant-design/x/es/sender';
import Prompts from '@ant-design/x/es/prompts';
import { 
    CopyOutlined, SyncOutlined, UserOutlined, RobotOutlined, 
    PlusOutlined, DeleteOutlined, MenuFoldOutlined, MenuUnfoldOutlined,
    MessageOutlined, CloseOutlined, SearchOutlined
} from "@ant-design/icons";
import { Button, Flex, Space, message as notification, List, Tooltip, Spin, Popconfirm, Typography, Input } from 'antd';
import { copyText } from '../../../context/helper';
import GenerateMessage from '../AIAgent/GenerateMessage';
import { AppContext } from '../../../context/ContextProvider';
import api from '../../../api';

/**
 * Parse and format the AI response
 * Removes command lines and cleans up JSON/dict output
 */
const parseAIResponse = (rawMessage) => {
    if (!rawMessage) return rawMessage;
    
    let cleanedMessage = rawMessage;
    
    // Remove "**Command:**" line and the kubectl command
    cleanedMessage = cleanedMessage.replace(/\*\*Command:\*\*\s*`[^`]+`/g, '');
    
    // Try to extract output from Python dict format: {'output': '...'}
    const dictMatch = cleanedMessage.match(/\{'output':\s*'([\s\S]+?)'\}/);
    if (dictMatch) {
        let output = dictMatch[1];
        output = output.replace(/\\n/g, '\n');
        output = output.replace(/\\t/g, '\t');
        output = output.replace(/\\'/g, "'");
        cleanedMessage = cleanedMessage.replace(/```[\s\S]*?\{'output':[\s\S]*?\}[\s\S]*?```/, '```\n' + output + '\n```');
    }
    
    cleanedMessage = cleanedMessage.replace(/\n{3,}/g, '\n\n');
    return cleanedMessage.trim();
};

const roles = {
    user: {
        avatar: { icon: <UserOutlined />, style: { background: '#fde3cf' } },
        typing: { step: 5, interval: 20 },
        placement: "end",
        styles: {
            content: {
                background: "#6486ee",
                color: "white",
                borderRadius: "20px",
                maxWidth: "100%",
                wordBreak: "break-word"
            }
        },
    },
    assistant: {
        placement: 'start',
        avatar: { icon: <RobotOutlined />, style: { background: '#87d068' } },
        styles: {
            content: {
                background: "white",
                border: "1px solid #e2e8f0",
                color: "#1e293b",
                borderRadius: "20px",
                maxWidth: "100%",
                width: "100%",
                overflowX: "visible",
                overflowY: "visible",
                wordBreak: "break-word"
            }
        }
    },
};

const promptItems = [
    { key: "1", description: "List all pods in the cluster" },
    { key: "2", description: "Show me all nodes and their status" },
    { key: "3", description: "Get all deployments in kube-system namespace" },
    { key: "4", description: "Show services in default namespace" }
];

const promptStyle = {
    title: {
        color: "#5e6366",
        fontSize: "32px",
        fontWeight: "500",
        fontFamily: "Poppins",
        textAlign: "center"
    },
    item: {
        border: "1px solid #6486ee",
        borderRadius: "10px",
    },
    itemContent: {
        color: "#6486ee",
        fontFamily: "Nunito Sans",
        fontWeight: "500",
        fontSize: "14px"
    }
};

/**
 * K8sAICopilot Component with Thread Management
 * AI Copilot for Kubernetes cluster operations with persistent chat threads
 * 
 * @param {Object} props
 * @param {string} props.clusterUniqueId - Base64 encoded cluster unique ID
 * @param {string} props.clusterName - Name of the cluster
 */
export default function K8sAICopilot({ clusterUniqueId, clusterName }) {
    // Thread management state
    const [currentThreadId, setCurrentThreadId] = useState(null);
    const [threads, setThreads] = useState([]);
    const [filteredThreads, setFilteredThreads] = useState([]);
    const [threadSearchQuery, setThreadSearchQuery] = useState('');
    const [showSidebar, setShowSidebar] = useState(true);
    const [loadingThreads, setLoadingThreads] = useState(false);
    const [loadingThreadId, setLoadingThreadId] = useState(null);
    
    // Chat state
    const [messages, setMessages] = useState([{
        content: `Hello! I'm your Kubernetes AI Copilot for **${clusterName}**. 

I can help you with:
• Listing resources (pods, nodes, services, deployments)
• Checking resource status and health
• Describing specific resources
• Getting logs and events
• Troubleshooting issues

Just ask me in plain English!`,
        role: "assistant"
    }]);
    const [message, setMessage] = useState("");
    const senderRef = useRef(null);
    const controllerRef = useRef(null);
    const timerRef = useRef(null);
    const [promptScroll, setPromptScroll] = useState(false);
    
    const { setOpenRightDrawer, profileData } = useContext(AppContext);
    
    // Get org_id from profile data (fallback to email-based ID if not available)
    // Convert to string to ensure compatibility with backend
    const orgId = String(profileData?.organisation?.id || profileData?.organisation || profileData?.email?.split('@')[0] || 'default-org');
    
    // Debug log
    useEffect(() => {
        console.log('[K8sAICopilot] Debug Info:', {
            clusterUniqueId,
            clusterName,
            orgId,
            profileData: profileData ? 'exists' : 'missing'
        });
    }, [clusterUniqueId, orgId, profileData]);
    
    /**
     * Fetch threads from backend
     */
    const fetchThreads = async () => {
        if (!orgId) return;
        
        setLoadingThreads(true);
        try {
            const response = await api.get(
                `${process.env.REACT_APP_KUBEGATEWAY}/api/chat/threads?org_id=${orgId}&cluster_unique_id=${clusterUniqueId}`
            );
            const threadsData = response.data.threads || [];
            setThreads(threadsData);
            setFilteredThreads(threadsData);
        } catch (error) {
            console.error('Failed to fetch threads:', error);
        } finally {
            setLoadingThreads(false);
        }
    };
    
    /**
     * Load a specific thread's message history
     */
    const loadThread = async (threadId) => {
        setLoadingThreadId(threadId);
        try {
            const response = await api.get(`${process.env.REACT_APP_KUBEGATEWAY}/api/chat/threads/${threadId}`);
            const threadData = response.data;
            
            // Convert thread messages to component format
            const loadedMessages = threadData.messages.map(msg => ({
                content: msg.content,
                role: msg.role
            }));
            
            setMessages(loadedMessages);
            setCurrentThreadId(threadId);
            notification.success('Thread loaded successfully');
        } catch (error) {
            console.error('Failed to load thread:', error);
            notification.error('Failed to load thread');
        } finally {
            setLoadingThreadId(null);
        }
    };
    
    /**
     * Create a new thread
     */
    const createNewThread = () => {
        setCurrentThreadId(null);
        setMessages([{
            content: `Hello! I'm your Kubernetes AI Copilot for **${clusterName}**. 

I can help you with:
• Listing resources (pods, nodes, services, deployments)
• Checking resource status and health
• Describing specific resources
• Getting logs and events
• Troubleshooting issues

Just ask me in plain English!`,
            role: "assistant"
        }]);
        notification.info('New thread created');
    };
    
    /**
     * Delete a thread
     */
    const deleteThread = async (threadId, e) => {
        e?.stopPropagation();
        
        try {
            await api.delete(`${process.env.REACT_APP_KUBEGATEWAY}/api/chat/threads/${threadId}?org_id=${orgId}`);
            
            // Refresh thread list
            fetchThreads();
            
            // If deleted thread was active, create new thread
            if (currentThreadId === threadId) {
                createNewThread();
            }
            
            notification.success('Thread deleted successfully');
        } catch (error) {
            console.error('Failed to delete thread:', error);
            notification.error('Failed to delete thread');
        }
    };
    
    /**
     * Handle action button click (confirm/cancel)
     */
    const handleActionClick = async (actionId) => {
        console.log('🔘 Action button clicked:', actionId);
        
        // Determine action type from ID
        const isCancel = actionId.endsWith(':cancel');
        const userMessage = isCancel ? '❌ Cancelled' : '✅ Confirmed';
        
        console.log('📤 Sending action with payload:', {
            message: userMessage,
            cluster_unique_id: clusterUniqueId,
            action_id: actionId,
            thread_id: currentThreadId,
            org_id: String(orgId)
        });
        
        // Send action to backend with action_id
        let placeholderIndex;
        
        // Add user action message
        setMessages(prev => {
            const newMsgs = [
                ...prev,
                { content: userMessage, role: 'user' },
                { content: "", role: 'assistant', loading: true, fetching: true }
            ];
            placeholderIndex = newMsgs.length - 1;
            return newMsgs;
        });
        
        try {
            const payload = {
                message: userMessage,
                cluster_unique_id: clusterUniqueId,
                action_id: actionId,  // Send the action_id
                thread_id: currentThreadId,
                org_id: String(orgId)
            };
            
            console.log('📡 Sending to API:', payload);
            
            const response = await fetch(`${process.env.REACT_APP_KUBEGATEWAY}/api/chat/stream`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('isAuth')}`
                },
                body: JSON.stringify(payload)
            });
            
            if (!response.ok) throw new Error('Network response was not ok');
            
            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let accumulatedText = "";
            
            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                
                const chunk = decoder.decode(value, { stream: true });
                const lines = chunk.split('\n');
                
                for (const line of lines) {
                    if (line.startsWith('data: ')) {
                        try {
                            const jsonStr = line.substring(6);
                            const chunk = JSON.parse(jsonStr);
                            
                            if (chunk.type === 'content') {
                                accumulatedText += chunk.message;
                                setMessages((prev) => {
                                    const copy = [...prev];
                                    copy[placeholderIndex] = {
                                        content: parseAIResponse(accumulatedText),
                                        role: 'assistant',
                                        loading: false,
                                        fetching: true
                                    };
                                    return copy;
                                });
                            } else if (chunk.type === 'end') {
                                setMessages((prev) => {
                                    const copy = [...prev];
                                    copy[placeholderIndex] = {
                                        content: parseAIResponse(accumulatedText),
                                        role: 'assistant',
                                        loading: false,
                                        fetching: false
                                    };
                                    return copy;
                                });
                                fetchThreads();
                                break;
                            }
                        } catch (parseError) {
                            console.error("Failed to parse chunk:", parseError);
                        }
                    }
                }
            }
        } catch (err) {
            console.error('Action execution error:', err);
            setMessages((prev) => {
                const copy = [...prev];
                copy[placeholderIndex] = {
                    content: `⚠️ Error: ${err.message}`,
                    role: 'assistant',
                    loading: false,
                    fetching: false
                };
                return copy;
            });
        }
    };
    
    /**
     * Handles sending messages to the K8s chat API with thread persistence
     */
    const handleSend = async (userText) => {
        let placeholderIndex;
        
        // Add user message and assistant placeholder
        setMessages(prev => {
            const newMsgs = [
                ...prev,
                { content: userText, role: 'user' },
                { content: "", role: 'assistant', loading: true, fetching: true }
            ];
            placeholderIndex = newMsgs.length - 1;
            return newMsgs;
        });

        try {
            // Cancel any ongoing request
            controllerRef.current?.abort();
            controllerRef.current = new AbortController();
            
            setMessage("");

            // Prepare request body, filtering out null/undefined values
            const requestBody = {
                message: userText,
                model: "nova-lite"
            };
            
            if (clusterUniqueId) requestBody.cluster_unique_id = clusterUniqueId;
            if (currentThreadId) requestBody.thread_id = currentThreadId;
            if (orgId) requestBody.org_id = orgId;

            const response = await fetch(`${process.env.REACT_APP_KUBEGATEWAY}/api/chat/stream`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${localStorage.getItem('isAuth')}`
                },
                body: JSON.stringify(requestBody),
                signal: controllerRef.current.signal
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = "";
            let accumulatedText = "";

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;

                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split('\n');
                buffer = lines.pop() || "";

                for (const line of lines) {
                    if (line.startsWith('data: ')) {
                        const jsonStr = line.substring(6).trim();
                        if (!jsonStr) continue;

                        try {
                            const chunk = JSON.parse(jsonStr);
                            
                            // Handle thread_id from backend
                            if (chunk.type === 'thread_id') {
                                setCurrentThreadId(chunk.thread_id);
                                // Refresh thread list to show new thread
                                fetchThreads();
                                continue;
                            }
                            
                            if (chunk.type === 'content') {
                                accumulatedText += chunk.message;
                                setMessages((prev) => {
                                    const copy = [...prev];
                                    copy[placeholderIndex] = {
                                        content: parseAIResponse(accumulatedText),
                                        role: 'assistant',
                                        loading: false,
                                        fetching: true
                                    };
                                    return copy;
                                });
                            } else if (chunk.type === 'end') {
                                setMessages((prev) => {
                                    const copy = [...prev];
                                    copy[placeholderIndex] = {
                                        content: parseAIResponse(accumulatedText),
                                        role: 'assistant',
                                        loading: false,
                                        fetching: false
                                    };
                                    return copy;
                                });
                                // Refresh thread list to update last message time
                                fetchThreads();
                                break;
                            } else if (chunk.type === 'error' || chunk.type === 'warning') {
                                // Display error/warning as message content
                                accumulatedText += chunk.message;
                                setMessages((prev) => {
                                    const copy = [...prev];
                                    copy[placeholderIndex] = {
                                        content: parseAIResponse(accumulatedText),
                                        role: 'assistant',
                                        loading: false,
                                        fetching: false
                                    };
                                    return copy;
                                });
                                // Break after error to stop processing
                                break;
                            } else if (chunk.type === 'confirmation') {
                                // Display confirmation with interactive buttons
                                accumulatedText += chunk.message;
                                setMessages((prev) => {
                                    const copy = [...prev];
                                    copy[placeholderIndex] = {
                                        content: parseAIResponse(accumulatedText),
                                        role: 'assistant',
                                        loading: false,
                                        fetching: false,
                                        actions: chunk.actions || []  // Store action buttons
                                    };
                                    return copy;
                                });
                                // Break after confirmation to stop processing
                                break;
                            }
                        } catch (parseError) {
                            console.error("Failed to parse chunk:", jsonStr, parseError);
                        }
                    }
                }
            }
        }
        catch (err) {
            setMessages((prev) => {
                const copy = [...prev];
                copy[placeholderIndex] = {
                    content: (err.name === "AbortError") 
                        ? `⚠️ Request cancelled by user.` 
                        : `⚠️ Error: ${err.message}. Please check if KubeGateway is running.`,
                    role: 'assistant',
                    loading: false,
                    fetching: false
                };
                return copy;
            });
        }
        finally {
            setMessage("");
        }
    };

    const handleChange = (value) => {
        if (value.length <= 5000) {
            setMessage(value);
        }
    };

    const regenerateContent = () => {
        setMessages(prev => {
            const newMsgs = [...prev];
            newMsgs.pop(); // Remove assistant response
            newMsgs.pop(); // Remove user message
            return newMsgs;
        });
        handleSend(messages[messages.length - 2].content);
    };

    const handleScroll = () => {
        setPromptScroll(true);
        clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => setPromptScroll(false), 1000);
    };

    /**
     * Handle thread search
     */
    const handleThreadSearch = (value) => {
        setThreadSearchQuery(value);
        if (!value.trim()) {
            setFilteredThreads(threads);
        } else {
            const filtered = threads.filter(thread =>
                thread.title.toLowerCase().includes(value.toLowerCase())
            );
            setFilteredThreads(filtered);
        }
    };


    useEffect(() => {
        const ele = document.querySelector(".k8s-ai-prompt-list");
        ele?.addEventListener("scroll", handleScroll);
        
        // Fetch threads on mount
        fetchThreads();
        
        return () => {
            controllerRef.current?.abort();
            ele?.removeEventListener("scroll", handleScroll);
        };
    }, []);

    // Update filtered threads when threads change
    useEffect(() => {
        if (threadSearchQuery.trim()) {
            const filtered = threads.filter(thread =>
                thread.title.toLowerCase().includes(threadSearchQuery.toLowerCase())
            );
            setFilteredThreads(filtered);
        } else {
            setFilteredThreads(threads);
        }
    }, [threads, threadSearchQuery]);

    const handleCancel = () => {
        setOpenRightDrawer({
            drawerType: null,
            drawerProps: {}
        });
    };

    const senderLoading = messages.find((m) => m.fetching) ? true : false;
    
    // Format date for thread list
    const formatDate = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffMs = now - date;
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
        
        if (diffDays === 0) {
            return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
        } else if (diffDays === 1) {
            return 'Yesterday';
        } else if (diffDays < 7) {
            return `${diffDays} days ago`;
        } else {
            return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        }
    };

    return (
        <>
            <style>{`
                .ant-bubble-item:hover .message-actions {
                    opacity: 1 !important;
                }
            `}</style>
            {/* Simple Header */}
            <div style={{
                position: 'absolute',
                zIndex: 10,
                width: '100%',
                top: 0,
                height: '84px',
                backgroundColor: '#132f44',
                borderBottom: '1px solid #e9e9e9',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '0 20px'
            }}>
                <div style={{
                    color: 'white',
                    fontSize: '18px',
                    fontWeight: '500',
                    fontFamily: 'Poppins'
                }}>
                    🤖 AI Copilot - {clusterName}
                </div>
                <Button
                    type="text"
                    icon={<CloseOutlined />}
                    onClick={handleCancel}
                    style={{
                        color: 'white',
                        fontSize: '18px'
                    }}
                />
            </div>
            
            <div style={{ 
                height: "calc(100% - 90px)", 
                marginTop: "90px", 
                display: 'flex',
                overflow: 'hidden',
                width: '100%',
                maxWidth: '100%'
            }}>
                {/* Left Sidebar - Thread List */}
                <div style={{
                    width: showSidebar ? '280px' : '0',
                    minWidth: showSidebar ? '280px' : '0',
                    transition: 'all 0.3s',
                    overflow: 'hidden',
                    borderRight: showSidebar ? '1px solid #e2e8f0' : 'none',
                    background: '#fafafa',
                    display: 'flex',
                    flexDirection: 'column'
                }}>
                    {showSidebar && (
                        <>
                            {/* Sidebar Header */}
                            <div style={{ 
                                padding: '16px', 
                                borderBottom: '1px solid #e2e8f0',
                                background: 'white'
                            }}>
                                <Button 
                                    type="primary" 
                                    block 
                                    icon={<PlusOutlined />}
                                    onClick={createNewThread}
                                    style={{ 
                                        marginBottom: '12px',
                                        borderRadius: '6px',
                                        fontWeight: '500'
                                    }}
                                >
                                    New Thread
                                </Button>
                                <Input
                                    placeholder="Search threads..."
                                    prefix={<SearchOutlined style={{ color: '#888' }} />}
                                    value={threadSearchQuery}
                                    onChange={(e) => handleThreadSearch(e.target.value)}
                                    allowClear
                                    style={{
                                        marginBottom: '8px',
                                        borderRadius: '6px'
                                    }}
                                />
                                <div style={{ 
                                    fontSize: '12px', 
                                    color: '#888', 
                                    textAlign: 'center' 
                                }}>
                                    {filteredThreads.length} of {threads.length} thread{threads.length !== 1 ? 's' : ''}
                                </div>
                            </div>
                            
                            {/* Thread List */}
                            <div style={{ 
                                flex: 1, 
                                overflowY: 'auto',
                                padding: '8px'
                            }}>
                                <Spin spinning={loadingThreads}>
                                    <List
                                        dataSource={filteredThreads}
                                        locale={{ emptyText: threadSearchQuery ? 'No threads found' : 'No threads yet. Start a new conversation!' }}
                                        renderItem={(thread) => (
                                            <List.Item
                                                style={{
                                                    cursor: loadingThreadId === thread.thread_id ? 'wait' : 'pointer',
                                                    background: currentThreadId === thread.thread_id ? '#e6f7ff' : 'white',
                                                    padding: '12px',
                                                    borderRadius: '8px',
                                                    marginBottom: '6px',
                                                    border: currentThreadId === thread.thread_id ? '2px solid #1890ff' : '1px solid #e2e8f0',
                                                    transition: 'all 0.2s',
                                                    opacity: loadingThreadId === thread.thread_id ? 0.6 : 1,
                                                    position: 'relative'
                                                }}
                                                onClick={() => !loadingThreadId && loadThread(thread.thread_id)}
                                            >
                                                {loadingThreadId === thread.thread_id && (
                                                    <div style={{
                                                        position: 'absolute',
                                                        top: '50%',
                                                        left: '50%',
                                                        transform: 'translate(-50%, -50%)',
                                                        zIndex: 10
                                                    }}>
                                                        <Spin size="small" />
                                                    </div>
                                                )}
                                                <List.Item.Meta
                                                    avatar={<MessageOutlined style={{ fontSize: '16px', color: '#1890ff' }} />}
                                                    title={
                                                        <Tooltip title={thread.title} placement="top">
                                                            <div style={{ 
                                                                fontSize: '13px', 
                                                                fontWeight: currentThreadId === thread.thread_id ? '600' : '500',
                                                                color: '#1e293b',
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                whiteSpace: 'nowrap'
                                                            }}>
                                                                {thread.title}
                                                            </div>
                                                        </Tooltip>
                                                    }
                                                    description={
                                                        <div style={{ 
                                                            fontSize: '11px', 
                                                            color: '#888',
                                                            display: 'flex',
                                                            justifyContent: 'space-between',
                                                            alignItems: 'center'
                                                        }}>
                                                            <span>{formatDate(thread.updated_at)}</span>
                                                            <span>{thread.message_count} msg{thread.message_count !== 1 ? 's' : ''}</span>
                                                        </div>
                                                    }
                                                />
                                                <div 
                                                    className="thread-delete-button"
                                                    style={{
                                                        opacity: 0,
                                                        transition: 'opacity 0.2s',
                                                        pointerEvents: loadingThreadId === thread.thread_id ? 'none' : 'auto'
                                                    }}
                                                    onMouseEnter={(e) => {
                                                        e.currentTarget.style.opacity = '1';
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        e.currentTarget.style.opacity = '0';
                                                    }}
                                                >
                                                    <Popconfirm
                                                        title="Delete this thread?"
                                                        description="This action cannot be undone."
                                                        onConfirm={(e) => deleteThread(thread.thread_id, e)}
                                                        okText="Delete"
                                                        cancelText="Cancel"
                                                        okButtonProps={{ danger: true }}
                                                    >
                                                        <DeleteOutlined 
                                                            onClick={(e) => e.stopPropagation()}
                                                            style={{ 
                                                                fontSize: '14px', 
                                                                color: '#ff4d4f',
                                                                cursor: 'pointer'
                                                            }}
                                                        />
                                                    </Popconfirm>
                                                </div>
                                                <style>{`
                                                    .thread-delete-button:hover {
                                                        opacity: 1 !important;
                                                    }
                                                    .ant-list-item:hover .thread-delete-button {
                                                        opacity: 1 !important;
                                                    }
                                                `}                                                </style>
                                            </List.Item>
                                        )}
                                    />
                                </Spin>
                            </div>
                        </>
                    )}
                </div>
                
                {/* Toggle Sidebar Button */}
                <div style={{
                    width: '40px',
                    background: '#fafafa',
                    borderRight: '1px solid #e2e8f0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}>
                    <Tooltip title={showSidebar ? "Hide threads" : "Show threads"} placement="right">
                        <Button 
                            type="text" 
                            icon={showSidebar ? <MenuFoldOutlined /> : <MenuUnfoldOutlined />}
                            onClick={() => setShowSidebar(!showSidebar)}
                            style={{ 
                                transform: 'rotate(0deg)',
                                height: '40px'
                            }}
                        />
                    </Tooltip>
                </div>
                
                {/* Main Chat Area */}
                <Flex vertical style={{ flex: 1, minWidth: 0, overflow: 'hidden', maxWidth: '100%' }}>
                    <XProvider>
                        <div 
                            style={{ 
                                height: "100%", 
                                overflowY: 'auto', 
                                overflowX: 'hidden',
                                padding: 16,
                                width: '100%',
                                maxWidth: '100%'
                            }}
                        >
                            <Bubble.List
                                roles={roles}
                                autoScroll={true}
                                style={{ height: "inherit" }}
                                items={messages.map((msg, i) => ({
                                    key: i,
                                    content: msg.content,
                                    role: msg.role,
                                    loading: msg.loading,
                                    messageRender: (content) => {
                                        return <GenerateMessage
                                            content={content}
                                            i={i}
                                        />
                                    },
                                    footer: msg.role === "assistant" && i !== 0 && !msg.loading ? (
                                        msg.actions && msg.actions.length > 0 ? (
                                            // Render action buttons for confirmations (always show if present)
                                            <Space style={{ marginTop: '12px' }}>
                                                {msg.actions.map((action, idx) => (
                                                    <Button
                                                        key={idx}
                                                        type={action.variant === 'primary' ? 'primary' : action.variant === 'danger' ? 'primary' : 'default'}
                                                        danger={action.variant === 'danger'}
                                                        onClick={() => handleActionClick(action.action_id)}
                                                        size="middle"
                                                    >
                                                        {action.label}
                                                    </Button>
                                                ))}
                                            </Space>
                                        ) : (
                                            // Regular footer with copy/regenerate buttons (show on hover for all messages, always visible for last)
                                            <div 
                                                className="message-actions"
                                                style={{
                                                    opacity: i === messages.length - 1 ? 1 : 0,
                                                    transition: 'opacity 0.2s'
                                                }}
                                            >
                                                <Space>
                                                    {i === messages.length - 1 && (
                                                        <Button 
                                                            color="default" 
                                                            title="Re-generate response" 
                                                            variant="text" 
                                                            size="small" 
                                                            icon={<SyncOutlined />} 
                                                            onClick={regenerateContent} 
                                                        />
                                                    )}
                                                    <Button
                                                        color="default"
                                                        variant="text"
                                                        size="small"
                                                        title="Copy message"
                                                        onClick={() => copyText(msg.content)}
                                                        icon={<CopyOutlined />}
                                                    />
                                                </Space>
                                            </div>
                                        )
                                    ) : undefined
                                }))}
                            />
                        </div>
                        {messages.length < 2 && <div onScroll={handleScroll}>
                            <Prompts
                                title="What can I help with?"
                                items={promptItems}
                                onItemClick={(item) => handleSend(item.data.description)}
                                classNames={{
                                    list: `k8s-ai-prompt-list ${promptScroll ? "scroll" : ""}`
                                }}
                                style={{
                                    padding: "20px",
                                }}
                                styles={promptStyle}
                            />
                        </div>}
                        <div style={{ position: 'relative' }}>
                            <Sender
                                ref={senderRef}
                                value={message}
                                onChange={handleChange}
                                onSubmit={handleSend}
                                loading={senderLoading}
                                placeholder="Ask about your cluster... (e.g., 'list all pods', 'show node status')"
                                autoSize={{ minRows: 3, maxRows: 3 }}
                                style={{
                                    borderRadius: "0",
                                    borderColor: "#9e9e9e transparent transparent",
                                }}
                                allowSpeech
                                actions={false}
                            />
                            <div style={{
                                position: 'absolute',
                                bottom: '8px',
                                right: '12px',
                                fontSize: '12px',
                                color: message.length > 4500 ? '#ff4d4f' : '#888',
                                background: 'rgba(255, 255, 255, 0.9)',
                                padding: '2px 6px',
                                borderRadius: '4px',
                                pointerEvents: 'none'
                            }}>
                                {message.length} / 5000
                            </div>
                        </div>
                    </XProvider>
                </Flex>
            </div>
        </>
    );
}

